import { Link, Navigate, createFileRoute } from '@tanstack/react-router'
import {
  Button,
  ButtonGroup,
  Card,
  Content as CardContent,
  Header as CardHeader,
  Heading as CardTitle,
  DropZone,
  FileTrigger,
  Content as IllustratedContent,
  Heading as IllustratedHeading,
  IllustratedMessage,
  Picker,
  PickerItem,
  Radio,
  RadioGroup,
  Text,
  TextField as Input,
} from '@react-spectrum/s2'
import { useState } from 'react'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Upload,
} from 'lucide-react'
import { createAdminRagDocumentUpload, getAdminDocuments } from '@rpc/admin.rpc'
import type { FormEvent } from 'react'
import {
  fullWidthButtonStyles,
  fullWidthDropZoneStyles,
  fullWidthStyles,
} from '@/styles/s2-macros'

export const Route = createFileRoute('/admin/upload')({
  loader: async () => {
    const documents = await getAdminDocuments()
    return {
      ragDocuments: documents.filter((doc: any) => doc.source === 'rag'),
    }
  },
  component: UploadPage,
})

const ACCEPTED_DOCUMENT_FILE_TYPES = ['.pdf']

export function isAcceptedDocumentFile(file: File) {
  return ACCEPTED_DOCUMENT_FILE_TYPES.some((ext) =>
    file.name.toLowerCase().endsWith(ext),
  )
}

export function getFirstFileFromList(fileList: FileList | null): File | null {
  return Array.from(fileList ?? [])[0] ?? null
}

export async function getFirstDroppedFile(event: {
  items: Array<{ kind: string; getFile: () => File | Promise<File> }>
}): Promise<File | null> {
  for (const item of event.items) {
    if (item.kind !== 'file') continue
    return await item.getFile()
  }
  return null
}

export function normalizeUploadMode(value: string): 'new' | 'revision' {
  return value === 'revision' ? 'revision' : 'new'
}

export function getDocumentDescription(doc: any) {
  return doc.version || doc.category || doc.catalogKey || doc.id
}

function UploadPage() {
  const { ragDocuments } = Route.useLoaderData()
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdWorkflow, setCreatedWorkflow] = useState<{
    documentId: string
    revisionId: string
  } | null>(null)

  const [title, setTitle] = useState('')
  const [edition, setEdition] = useState('')
  const [geography, setGeography] = useState('England')
  const [issuer, setIssuer] = useState('')
  const [documentType, setDocumentType] = useState('')
  const [shortCode, setShortCode] = useState('')
  const [catalogKey, setCatalogKey] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploadMode, setUploadMode] = useState<'new' | 'revision'>('new')
  const [existingDocumentId, setExistingDocumentId] = useState<string | null>(
    null,
  )

  const setSelectedFile = (selectedFile: File) => {
    if (!isAcceptedDocumentFile(selectedFile)) {
      setError('Please select a PDF file only.')
      return
    }

    setError(null)
    setFile(selectedFile)
    if (!title.trim()) {
      setTitle(
        selectedFile.name
          .replace(/\.[^.]+$/, '')
          .replace(/[-_]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim(),
      )
    }
  }

  const handleFileSelect = (fileList: FileList | null) => {
    const selected = getFirstFileFromList(fileList)
    if (selected) setSelectedFile(selected)
  }

  const handleDropZoneFile = async (event: { items: Array<any> }) => {
    const droppedFile = await getFirstDroppedFile(event)
    if (droppedFile) setSelectedFile(droppedFile)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!file) return

    setIsUploading(true)
    setError(null)

    const formData = new FormData()
    formData.append('files', file)
    formData.append('title', title)
    formData.append('edition', edition)
    formData.append('geography', geography)
    formData.append('issuer', issuer)
    formData.append('documentType', documentType)
    formData.append('shortCode', shortCode)
    formData.append('catalogKey', catalogKey)
    if (uploadMode === 'revision' && existingDocumentId) {
      formData.append('existingDocumentId', existingDocumentId)
    }

    try {
      const created = await createAdminRagDocumentUpload({ data: formData })
      setCreatedWorkflow({
        documentId: created.documentId,
        revisionId: created.revisionId,
      })
    } catch (uploadError: any) {
      setError(uploadError?.message || 'Upload failed')
      setIsUploading(false)
    }
  }

  if (createdWorkflow) {
    return (
      <Navigate
        to="/admin/documents/$documentId/revisions/$revisionId"
        params={createdWorkflow}
      />
    )
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <Link
        to="/admin"
        className="mb-6 inline-flex items-center text-sm text-neutral-500 transition-colors hover:text-neutral-900 dark:hover:text-neutral-300"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to Dashboard
      </Link>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <CardTitle>Upload chatbot document</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <RadioGroup
                label="Upload target"
                value={uploadMode}
                onChange={(value) => {
                  const nextMode = normalizeUploadMode(value)
                  setUploadMode(nextMode)
                  if (nextMode === 'new') setExistingDocumentId(null)
                  if (
                    nextMode === 'revision' &&
                    !existingDocumentId &&
                    ragDocuments.length === 1
                  ) {
                    setExistingDocumentId(ragDocuments[0].id)
                  }
                }}
                orientation="horizontal"
                isDisabled={isUploading}
              >
                <Radio value="new">New document</Radio>
                <Radio value="revision" isDisabled={ragDocuments.length === 0}>
                  New revision of existing document
                </Radio>
              </RadioGroup>

              {uploadMode === 'revision' && (
                <Picker
                  label="Existing document"
                  items={ragDocuments}
                  value={existingDocumentId ?? undefined}
                  onChange={setExistingDocumentId as (key: any) => void}
                  isDisabled={isUploading}
                >
                  {(doc: any) => (
                    <PickerItem id={doc.id} textValue={doc.title}>
                      <Text slot="label">{doc.title}</Text>
                      <Text slot="description">
                        {getDocumentDescription(doc)}
                      </Text>
                    </PickerItem>
                  )}
                </Picker>
              )}

              <div className="space-y-2">
                <label htmlFor="title">Document title</label>
                <Input
                  id="title"
                  name="title"
                  aria-label="Document title"
                  isRequired={uploadMode === 'new'}
                  placeholder="e.g. LABC Warranty V13 Technical Manual"
                  value={title}
                  onChange={setTitle}
                  isDisabled={isUploading}
                />
                {uploadMode === 'revision' && (
                  <p className="text-xs text-neutral-500">
                    Leave blank to keep the existing document title.
                  </p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="issuer">Issuer</label>
                  <Input
                    id="issuer"
                    name="issuer"
                    aria-label="Issuer"
                    placeholder="e.g. LABC Warranty"
                    value={issuer}
                    onChange={setIssuer}
                    isDisabled={isUploading}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="edition">Edition</label>
                  <Input
                    id="edition"
                    name="edition"
                    aria-label="Edition"
                    isRequired
                    placeholder="e.g. Version 13"
                    value={edition}
                    onChange={setEdition}
                    isDisabled={isUploading}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="document-type">Document type</label>
                  <Input
                    id="document-type"
                    name="documentType"
                    aria-label="Document type"
                    placeholder="e.g. technical_manual"
                    value={documentType}
                    onChange={setDocumentType}
                    isDisabled={isUploading}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="short-code">Short code</label>
                  <Input
                    id="short-code"
                    name="shortCode"
                    aria-label="Short code"
                    placeholder="Optional display code"
                    value={shortCode}
                    onChange={setShortCode}
                    isDisabled={isUploading}
                  />
                </div>
              </div>

              <div className="grid gap-4 border-t border-neutral-100 pt-4 dark:border-neutral-800 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="geography">Geography</label>
                  <Input
                    id="geography"
                    name="geography"
                    aria-label="Geography"
                    isRequired
                    value={geography}
                    onChange={setGeography}
                    isDisabled={isUploading}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="catalog-key">Catalog key</label>
                  <Input
                    id="catalog-key"
                    name="catalogKey"
                    aria-label="Catalog key"
                    placeholder="Optional stable key"
                    value={catalogKey}
                    onChange={setCatalogKey}
                    isDisabled={isUploading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label id="file-upload-label">PDF file</label>
                <DropZone
                  aria-labelledby="file-upload-label"
                  styles={fullWidthDropZoneStyles}
                  isFilled={Boolean(file)}
                  onDrop={handleDropZoneFile}
                >
                  <IllustratedMessage>
                    <Upload className="mx-auto h-10 w-10 text-neutral-400" />
                    <IllustratedHeading>Drop one PDF here</IllustratedHeading>
                    <IllustratedContent>
                      The document will be indexed for chatbot search only.
                    </IllustratedContent>
                    <ButtonGroup>
                      <FileTrigger
                        acceptedFileTypes={ACCEPTED_DOCUMENT_FILE_TYPES}
                        onSelect={handleFileSelect}
                      >
                        <Button variant="secondary" isDisabled={isUploading}>
                          Select PDF
                        </Button>
                      </FileTrigger>
                    </ButtonGroup>
                  </IllustratedMessage>
                </DropZone>
                <p className="text-xs text-neutral-500">
                  {file
                    ? file.name
                    : 'PDF only. Upload one document per workflow.'}
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  <AlertCircle className="mt-0.5 h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                styles={fullWidthButtonStyles}
                isDisabled={
                  isUploading ||
                  !file ||
                  !edition.trim() ||
                  (uploadMode === 'new' && !title.trim()) ||
                  (uploadMode === 'revision' && !existingDocumentId)
                }
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating workflow...
                  </>
                ) : (
                  'Upload and review pages'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card styles={fullWidthStyles}>
          <CardHeader>
            <CardTitle>What happens next</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-4 text-sm text-neutral-700 dark:text-neutral-300">
              <li className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-neutral-900 dark:text-neutral-100" />
                <span>Upload the PDF and create a draft revision.</span>
              </li>
              <li className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-neutral-900 dark:text-neutral-100" />
                <span>Review suggested front/back matter pages to skip.</span>
              </li>
              <li className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-neutral-900 dark:text-neutral-100" />
                <span>
                  Run page extraction, chunking, and text/image embeddings.
                </span>
              </li>
              <li className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-neutral-900 dark:text-neutral-100" />
                <span>
                  Verify the new revision with a scoped search before relying on
                  it.
                </span>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
